// The productions in the sidebar, in your own order (as in Notion): drag a row to where you want it, or use its
// ⋯ menu (a right-click opens it too) or Ctrl+Shift+↑/↓. The production in view opens up into its folder tree:
// `slotRef` is where ProjectView puts it.

import {
  type PointerEvent as ReactPointerEvent,
  type Ref,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { dropIndex, moveId } from "../data/order.ts";
import { doneText } from "../data/steps.ts";
import type { ProductionState } from "../data/useSteps.ts";
import { atLeast } from "../lib.ts";
import { formatRoute } from "../route.ts";
import type { Project } from "../types.ts";
import { errText, MOD, useApp } from "./app.ts";
import { Icon } from "./icons.tsx";
import { Strip } from "./kit.tsx";
import { Menu } from "./Menu.tsx";
import { ProjectPicture } from "./ProjectPicture.tsx";
import { RenameField } from "./RenameField.tsx";
import { DeleteProduction } from "./screens/Home.tsx";

/** How far the pointer travels before a press becomes a drag (a shorter press stays a click). */
const DRAG_FROM = 4;
/** Within this distance of the list's top or bottom edge the list scrolls along. */
const EDGE = 36;
const SETTLE = { duration: 220, easing: "cubic-bezier(0.2, 0, 0, 1)" };

interface Drag {
  id: string;
  from: number;
  x0: number;
  y0: number;
  x: number;
  y: number;
  on: boolean;
  /** the gap the row hovers: 0 before the first row, n after the last */
  slot: number;
  /** where the pointer holds the row */
  dx: number;
  dy: number;
  ghost: HTMLElement | null;
  raf: number;
}

const calm = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/** Slide an element from where it was (`from`) to where it is now. */
function settle(el: HTMLElement, from: DOMRect) {
  const now = el.getBoundingClientRect();
  const dx = from.left - now.left;
  const dy = from.top - now.top;
  if ((dx || dy) && !calm() && el.animate)
    el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }], SETTLE);
}

export function SideProductions({
  productions,
  states,
  activeProject,
  slotRef,
  slotId,
  folded,
  onFold,
  onReorder,
}: {
  productions: readonly Project[];
  states: ReadonlyMap<string, ProductionState>;
  activeProject: string | null;
  slotRef: Ref<HTMLDivElement>;
  slotId: string;
  folded: ReadonlySet<string>;
  onFold: (id: string) => void;
  /** the production ids in their new order */
  onReorder: (ids: readonly string[]) => void;
}) {
  const { api, ws, role, onProjectChanged } = useApp();
  const canManage = atLeast(role, "producer");
  const list = useRef<HTMLUListElement>(null);
  const line = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Project | null>(null);
  const [said, setSaid] = useState("");
  /** where each row was before a move, so it can slide to its new place */
  const flip = useRef<Map<string, DOMRect> | null>(null);
  const ids = productions.map((p) => p.id);
  // the window listeners of a drag read the list as it is now
  const latest = useRef({ ids, productions, onReorder });
  latest.current = { ids, productions, onReorder };

  const rows = () => [...(list.current?.querySelectorAll<HTMLElement>(":scope > li") ?? [])];
  const rowRect = (li: HTMLElement) => (li.firstElementChild as HTMLElement).getBoundingClientRect();

  const move = (id: string, to: number, ghost?: DOMRect) => {
    const { ids: now, productions: ps, onReorder: save } = latest.current;
    const next = moveId(now, id, to);
    if (next.join() === now.join()) return;
    flip.current = new Map(rows().map((li) => [li.dataset.id!, li.getBoundingClientRect()]));
    if (ghost) flip.current.set(id, ghost);
    save(next);
    const name = ps.find((p) => p.id === id)?.name ?? "The production";
    setSaid(`${name} moved to place ${next.indexOf(id) + 1} of ${next.length}`);
  };

  const key = ids.join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: the id list is what moved
  useLayoutEffect(() => {
    const first = flip.current;
    flip.current = null;
    if (!first) return;
    for (const li of rows()) {
      const was = first.get(li.dataset.id!);
      if (was) settle(li, was);
    }
  }, [key]);

  // ── drag ──────────────────────────────────────────────────────────

  /** Put the ghost under the pointer and the drop line in the gap it hovers. */
  const place = (d: Drag) => {
    if (d.ghost) d.ghost.style.transform = `translate(${d.x - d.dx}px, ${d.y - d.dy}px)`;
    const rs = rows();
    let slot = rs.length;
    for (let i = 0; i < rs.length; i++) {
      const r = rowRect(rs[i]!);
      if (d.y < r.top + r.height / 2) {
        slot = i;
        break;
      }
    }
    d.slot = slot;
    const ln = line.current;
    const ul = list.current;
    if (!ln || !ul) return;
    if (dropIndex(d.from, slot) === null || rs.length === 0) {
      ln.classList.remove("on");
      return;
    }
    const y =
      slot < rs.length
        ? rs[slot]!.getBoundingClientRect().top - 2
        : rs[rs.length - 1]!.getBoundingClientRect().bottom;
    ln.style.transform = `translateY(${Math.round(y - ul.getBoundingClientRect().top)}px)`;
    ln.classList.add("on");
  };

  /** Near the top or bottom edge, the list scrolls along, faster the closer the pointer gets. */
  const tick = () => {
    const d = drag.current;
    if (!d?.on) return;
    const sc = list.current?.closest<HTMLElement>(".side-scroll");
    if (sc) {
      const r = sc.getBoundingClientRect();
      const v =
        d.y < r.top + EDGE
          ? -Math.ceil((r.top + EDGE - d.y) / 4)
          : d.y > r.bottom - EDGE
            ? Math.ceil((d.y - r.bottom + EDGE) / 4)
            : 0;
      if (v) {
        sc.scrollTop += v;
        place(d);
      }
    }
    d.raf = requestAnimationFrame(tick);
  };

  const lift = (d: Drag) => {
    const li = rows()[d.from];
    const row = li?.firstElementChild as HTMLElement | undefined;
    if (!row) return;
    const r = row.getBoundingClientRect();
    d.dx = d.x0 - r.left;
    d.dy = d.y0 - r.top;
    const g = row.cloneNode(true) as HTMLElement;
    g.classList.add("side-ghost");
    g.setAttribute("aria-hidden", "true");
    g.inert = true;
    const cs = getComputedStyle(row);
    g.style.width = `${r.width}px`;
    g.style.font = cs.font;
    g.style.color = cs.color;
    document.body.append(g);
    d.ghost = g;
    d.on = true;
    document.documentElement.classList.add("prod-dragging");
    window.getSelection()?.removeAllRanges();
    setMenuFor(null);
    setDragging(d.id);
    place(d);
    d.raf = requestAnimationFrame(tick);
  };

  /** End the drag; the ghost's last rectangle, so the row can settle from there. */
  const drop = (d: Drag): DOMRect | null => {
    cancelAnimationFrame(d.raf);
    const at = d.ghost?.getBoundingClientRect() ?? null;
    d.ghost?.remove();
    line.current?.classList.remove("on");
    document.documentElement.classList.remove("prod-dragging");
    setDragging(null);
    return at;
  };

  const stop = () => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onCancel);
    window.removeEventListener("keydown", onKey, true);
    drag.current = null;
  };

  function onMove(e: PointerEvent) {
    const d = drag.current;
    if (!d) return;
    d.x = e.clientX;
    d.y = e.clientY;
    if (!d.on) {
      if (Math.hypot(d.x - d.x0, d.y - d.y0) < DRAG_FROM) return;
      lift(d);
    }
    e.preventDefault();
    place(d);
  }

  function onUp() {
    const d = drag.current;
    stop();
    if (!d?.on) return; // a plain click: the link takes it
    // the click that ends a drag opens nothing
    const swallow = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("click", swallow, { capture: true, once: true });
    setTimeout(() => window.removeEventListener("click", swallow, true), 0);
    const at = dropIndex(d.from, d.slot);
    const from = drop(d);
    if (at !== null) move(d.id, at, from ?? undefined);
    else if (from) {
      const li = rows().find((x) => x.dataset.id === d.id);
      if (li) settle(li, from);
    }
  }

  /** Escape or a lost pointer: the row goes back where it was. */
  function onCancel() {
    const d = drag.current;
    stop();
    if (!d?.on) return;
    const from = drop(d);
    const li = rows().find((x) => x.dataset.id === d.id);
    if (li && from) settle(li, from);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key !== "Escape" || !drag.current?.on) return;
    e.preventDefault();
    e.stopPropagation();
    onCancel();
  }

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>, id: string) => {
    // a finger scrolls the sidebar; it moves rows with the ⋯ menu
    if (e.button !== 0 || e.pointerType === "touch" || renaming || drag.current) return;
    if ((e.target as HTMLElement).closest(".side-prod-more, input")) return;
    drag.current = {
      id,
      from: latest.current.ids.indexOf(id),
      x0: e.clientX,
      y0: e.clientY,
      x: e.clientX,
      y: e.clientY,
      on: false,
      slot: -1,
      dx: 0,
      dy: 0,
      ghost: null,
      raf: 0,
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKey, true);
  };

  // leaving the sidebar mid-drag (a route change, sign-out) leaves no ghost behind
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on unmount
  useEffect(
    () => () => {
      const d = drag.current;
      if (!d) return;
      stop();
      if (d.on) drop(d);
    },
    [],
  );

  // ── the row's menu ───────────────────────────────────────────────

  const linkOf = (p: Project) => formatRoute({ view: "project", projectId: p.id, loc: { kind: "root" } });
  const fullLink = (href: string) => `${location.origin}${location.pathname}${href}`;

  const rename = async (p: Project, name: string | null) => {
    setRenaming(null);
    if (!name) return;
    try {
      const next = await api.request<Project>("PATCH", `/workspaces/${ws}/projects/${p.id}`, {
        body: { name },
      });
      onProjectChanged?.(next);
    } catch (x) {
      setSaid(`${p.name} was not renamed: ${errText(x)}`);
    }
  };

  if (productions.length === 0) return <p className="side-none">Your productions will show up here.</p>;

  return (
    <div className="side-prods-wrap">
      <ul className="side-prods" ref={list}>
        {productions.map((p, i) => {
          const on = p.id === activeProject;
          const st = states.get(p.id);
          const isFolded = folded.has(p.id);
          const href = linkOf(p);
          const open = menuFor === p.id;
          const last = productions.length - 1;
          return (
            <li key={p.id} data-id={p.id} className={dragging === p.id ? "is-dragging" : undefined}>
              {/* biome-ignore lint/a11y/noStaticElementInteractions: dragging and the right-click are shortcuts for the ⋯ menu and Ctrl+Shift+↑/↓ */}
              <div
                className={open ? "side-prod-row menu-open" : "side-prod-row"}
                onPointerDown={(e) => onPointerDown(e, p.id)}
                onContextMenu={(e) => {
                  if (renaming === p.id) return;
                  e.preventDefault();
                  setMenuFor(p.id);
                }}
              >
                {on ? (
                  <button
                    type="button"
                    className="side-fold"
                    aria-expanded={!isFolded}
                    aria-controls={slotId}
                    aria-label={`${isFolded ? "Show" : "Hide"} folders of ${p.name}`}
                    onClick={() => onFold(p.id)}
                  >
                    <Icon name="chevron" size={12} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="side-fold"
                    aria-label={`Open ${p.name}`}
                    onClick={() => {
                      if (isFolded) onFold(p.id);
                      location.hash = href;
                    }}
                  >
                    <Icon name="chevron" size={12} />
                  </button>
                )}
                {renaming === p.id ? (
                  <div className="side-link side-prod">
                    {p.image_asset_version_id && <ProjectPicture project={p} size={16} />}
                    <RenameField
                      value={p.name}
                      label={`Rename ${p.name}`}
                      onDone={(n) => void rename(p, n)}
                    />
                  </div>
                ) : (
                  <a
                    className="side-link side-prod"
                    href={href}
                    draggable={false}
                    aria-current={on ? "true" : undefined}
                    aria-keyshortcuts="Control+Shift+ArrowUp Control+Shift+ArrowDown"
                    onClick={() => {
                      if (isFolded) onFold(p.id);
                    }}
                    onKeyDown={(e) => {
                      if (!e.shiftKey || !(e.ctrlKey || e.metaKey)) return;
                      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                        e.preventDefault();
                        move(p.id, i + (e.key === "ArrowUp" ? -1 : 1));
                      }
                    }}
                  >
                    {p.image_asset_version_id && <ProjectPicture project={p} size={16} />}
                    <span className="side-prod-name">{p.name}</span>
                    {st && (
                      <span className="side-prod-strip">
                        <Strip steps={st.steps} size={5} label={`${p.name}: ${doneText(st.steps)}`} />
                      </span>
                    )}
                  </a>
                )}
                {renaming !== p.id && (
                  <div className="side-prod-more">
                    <Menu
                      fixed
                      label={`${p.name} actions`}
                      buttonClass="side-prod-more-btn"
                      button={<Icon name="more" size={14} />}
                      open={open}
                      onOpenChange={(o) => setMenuFor(o ? p.id : null)}
                    >
                      {(close) => (
                        <>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              close();
                              window.open(fullLink(href), "_blank", "noopener");
                            }}
                          >
                            Open in new tab
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              close();
                              navigator.clipboard?.writeText(fullLink(href)).then(
                                () => setSaid("Link copied"),
                                () => setSaid("The link could not be copied"),
                              );
                            }}
                          >
                            Copy link
                          </button>
                          {canManage && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                close();
                                setRenaming(p.id);
                              }}
                            >
                              Rename
                            </button>
                          )}
                          <hr className="menu-sep" />
                          <button
                            type="button"
                            role="menuitem"
                            disabled={i === 0}
                            onClick={() => {
                              close();
                              move(p.id, i - 1);
                            }}
                          >
                            <span className="grow">Move up</span>
                            <kbd>{MOD} ⇧ ↑</kbd>
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={i === last}
                            onClick={() => {
                              close();
                              move(p.id, i + 1);
                            }}
                          >
                            <span className="grow">Move down</span>
                            <kbd>{MOD} ⇧ ↓</kbd>
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={i === 0}
                            onClick={() => {
                              close();
                              move(p.id, 0);
                            }}
                          >
                            Move to top
                          </button>
                          {canManage && (
                            <>
                              <hr className="menu-sep" />
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  close();
                                  location.hash = formatRoute({
                                    view: "project",
                                    projectId: p.id,
                                    loc: { kind: "settings" },
                                  });
                                }}
                              >
                                Settings
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  close();
                                  setDeleting(p);
                                }}
                              >
                                Delete production…
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </Menu>
                  </div>
                )}
              </div>
              {/* folded with `hidden`, never unmounted: ProjectView puts its tree inline without the slot */}
              {on && <div id={slotId} className="side-slot" ref={slotRef} hidden={isFolded} />}
            </li>
          );
        })}
      </ul>
      <div className="side-drop" ref={line} aria-hidden="true" />
      <p className="sr-only" aria-live="polite">
        {said}
      </p>
      {deleting && <DeleteProduction project={deleting} onClose={() => setDeleting(null)} />}
    </div>
  );
}
